import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { barberProfileApi } from '../services/barberProfileApi';

// Custom event name for Inventory setting changes
export const INVENTORY_CHANGED_EVENT = 'inventorySettingChanged';

// Helper function to dispatch the event when inventory setting changes
export const dispatchInventoryChanged = () => {
  window.dispatchEvent(new CustomEvent(INVENTORY_CHANGED_EVENT));
};

/**
 * Custom hook to check if the current user has Inventory Management enabled
 * Checks the barber profile's inventoryEnabled setting
 * Automatically refreshes when the setting changes via custom event
 * @returns {boolean} true if user has inventory enabled in profile settings, false otherwise
 */
export const useInventoryAccess = (): boolean => {
  const { state } = useAuth();
  const [hasInventory, setHasInventory] = useState(false);

  const checkInventoryAccess = useCallback(async () => {
    // Only check for Barber and ShopOwner roles
    if (!state.isAuthenticated || (state.user?.role !== 'Barber' && state.user?.role !== 'ShopOwner')) {
      setHasInventory(false);
      return;
    }

    try {
      // Small delay to ensure backend has processed the change
      // This helps avoid race conditions when called immediately after activation
      await new Promise(resolve => setTimeout(resolve, 100));

      // Fetch barber profile to check inventoryEnabled setting
      const profile = await barberProfileApi.getMyProfile();
      setHasInventory(profile.inventoryEnabled === true);
    } catch (error) {
      console.error('Error checking Inventory access:', error);
      setHasInventory(false);
    }
  }, [state.isAuthenticated, state.user?.role]);

  // Initial check on mount and when auth state changes
  useEffect(() => {
    checkInventoryAccess();
  }, [checkInventoryAccess]);

  // Listen for inventory setting change events
  useEffect(() => {
    const handleInventoryChange = () => {
      checkInventoryAccess();
    };

    window.addEventListener(INVENTORY_CHANGED_EVENT, handleInventoryChange);

    return () => {
      window.removeEventListener(INVENTORY_CHANGED_EVENT, handleInventoryChange);
    };
  }, [checkInventoryAccess]);

  return hasInventory;
};
